import { useEffect, useState, useRef } from 'react';
import { GlowOrb } from './NavBar';
import { fleetHeroFrames } from '../images';

function HeroParticles() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let w = canvas.offsetWidth;
    let h = canvas.offsetHeight;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = w * dpr;
    canvas.height = h * dpr;
    ctx.scale(dpr, dpr);

    const particles: {
      x: number; y: number; vx: number; vy: number; size: number; alpha: number; life: number;
    }[] = [];

    const spawn = () => {
      if (particles.length < 50) {
        particles.push({
          x: Math.random() * w,
          y: h + Math.random() * 20,
          vx: (Math.random() - 0.5) * 0.3,
          vy: -0.3 - Math.random() * 0.6,
          size: 0.5 + Math.random() * 1.5,
          alpha: 0.1 + Math.random() * 0.3,
          life: 1,
        });
      }
    };

    let animId: number;
    const animate = () => {
      ctx.clearRect(0, 0, w, h);
      spawn();

      for (let i = particles.length - 1; i >= 0; i--) {
        const p = particles[i];
        p.x += p.vx;
        p.y += p.vy;
        p.life -= 0.003;
        p.alpha *= p.life;

        if (p.life <= 0 || p.y < -10) {
          particles.splice(i, 1);
          continue;
        }

        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(217, 162, 78, ${p.alpha})`;
        ctx.fill();
      }

      animId = requestAnimationFrame(animate);
    };

    animId = requestAnimationFrame(animate);

    const handleResize = () => {
      w = canvas.offsetWidth;
      h = canvas.offsetHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    const ro = new ResizeObserver(handleResize);
    ro.observe(canvas);

    return () => {
      cancelAnimationFrame(animId);
      ro.disconnect();
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 w-full h-full pointer-events-none z-[1]"
    />
  );
}

function MouseGlow() {
  const divRef = useRef<HTMLDivElement>(null);
  const mouseRef = useRef({ x: -999, y: -999 });
  const rafRef = useRef<number>(0);

  useEffect(() => {
    const handleMove = (e: MouseEvent) => {
      mouseRef.current = { x: e.clientX, y: e.clientY };
    };
    window.addEventListener('mousemove', handleMove);

    const animate = () => {
      if (divRef.current) {
        divRef.current.style.transform = `translate(${mouseRef.current.x - 150}px, ${mouseRef.current.y - 150}px)`;
      }
      rafRef.current = requestAnimationFrame(animate);
    };
    rafRef.current = requestAnimationFrame(animate);

    return () => {
      window.removeEventListener('mousemove', handleMove);
      cancelAnimationFrame(rafRef.current);
    };
  }, []);

  return (
    <div
      ref={divRef}
      className="fixed top-0 left-0 w-[300px] h-[300px] rounded-full pointer-events-none z-0"
      style={{
        background: 'radial-gradient(circle, rgba(217,162,78,0.06) 0%, transparent 70%)',
        willChange: 'transform',
      }}
    />
  );
}

export default function HeroSection() {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (!imageLoaded) setImageError(true);
    }, 8000);
    return () => clearTimeout(timer);
  }, [imageLoaded]);

  return (
    <section className="relative h-[680px] md:h-[760px] lg:h-[800px] overflow-hidden pt-16">
      <HeroParticles />
      <GlowOrb className="top-10 left-[10%] w-[500px] h-[500px] bg-primary-500/8" />
      <GlowOrb className="bottom-10 right-[15%] w-[400px] h-[400px] bg-primary-500/5" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 h-full">
        <div className="grid lg:grid-cols-2 gap-8 h-full items-center">
          <div className="flex flex-col justify-center">
            <p className="text-xs font-mono tracking-[0.25em] text-primary-400 uppercase mb-4">
              Spatial Intelligence Infrastructure
            </p>

            <h1 className="text-4xl md:text-5xl lg:text-[3.5rem] font-heading font-bold text-foreground-50 leading-[1.15] mb-5">
              <span className="animate-word-reveal inline-block" style={{ animationDelay: '0.1s' }}>让机器</span>
              <br />
              <span className="text-primary-400 animate-word-reveal inline-block" style={{ animationDelay: '0.35s' }}>读懂物理世界</span>
            </h1>

            <p className="text-base text-foreground-400 leading-relaxed max-w-md mb-6">
              面向 Physical AI 时代，做中国的空间数据基础设施。
              统一管理异构终端舰队——人、AI 眼镜、机器狗、机器人、无人机、无人车。
            </p>

            <div className="flex items-center gap-3 mb-8">
              <button
                onClick={() => window.REACT_APP_NAVIGATE('/login')}
                className="flex items-center gap-2 px-6 py-2.5 rounded-xl bg-primary-500 hover:bg-primary-400 text-background-950 text-sm font-semibold transition-all cursor-pointer whitespace-nowrap btn-lift"
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-shield-user-line" />
                </span>
                登录控制台
              </button>
              <button
                onClick={() => window.REACT_APP_NAVIGATE('/console')}
                className="flex items-center gap-2 px-6 py-2.5 rounded-xl border border-background-700 hover:border-primary-500/40 text-foreground-200 text-sm font-medium transition-all cursor-pointer whitespace-nowrap btn-lift"
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-dashboard-line" />
                </span>
                进入控制台
              </button>
            </div>

            <div className="space-y-2 max-w-md">
              {[
                { icon: 'ri-brain-line', label: '人 · 认知与决策核心', tag: 'COGNITION' },
                { icon: 'ri-glasses-line', label: '镜 · 交互与感知中枢', tag: 'INTERFACE' },
                { icon: 'ri-robot-2-line', label: '犬 · 移动与执行终端', tag: 'ACTUATION' },
              ].map((item, i) => (
                <div
                  key={item.tag}
                  className="flex items-center gap-3 px-4 py-2.5 rounded-lg border border-primary-500/15 bg-background-900/30 hover:bg-primary-500/5 transition-all cursor-default animate-fade-in-up"
                  style={{ animationDelay: `${0.6 + i * 0.15}s` }}
                >
                  <span className="w-8 h-8 flex items-center justify-center rounded-md bg-primary-500/10">
                    <i className={`${item.icon} text-base text-primary-400`} />
                  </span>
                  <span className="text-sm text-foreground-300 flex-1">{item.label}</span>
                  <span className="text-[10px] font-mono text-primary-400/50">{item.tag}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="relative hidden lg:block h-full">
            <div className="relative h-full w-full flex items-center justify-center">
              {/* 与 hero 素材同为 3:2:比例对不上时 object-cover 会裁边,首当其冲的
                  就是素材底部那条 HUD 文字(截图里被切掉一半的那句)。 */}
              <div className="relative w-full max-w-[540px] aspect-[3/2]">
                <div className="absolute inset-0 -m-8 rounded-3xl bg-primary-500/5 blur-3xl animate-pulse" />
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[360px] h-[360px] rounded-full border border-primary-500/10 animate-pulse-ring" />
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[440px] h-[440px] rounded-full border border-primary-500/5 animate-pulse-ring-slow" />

                <div className="relative z-10 w-full h-full rounded-2xl border border-background-800/60 bg-background-900 overflow-hidden animate-hero-breathe">
                  {imageError && !imageLoaded ? (
                    <div className="relative w-full h-full flex items-center justify-center overflow-hidden">
                      <div className="absolute inset-0 opacity-20" style={{
                        backgroundImage: 'linear-gradient(rgba(217,162,78,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(217,162,78,0.1) 1px, transparent 1px)',
                        backgroundSize: '40px 40px'
                      }} />
                      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[300px] h-[300px] animate-[spin_20s_linear_infinite]">
                        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-10 h-10 rounded-lg border border-primary-500/20 bg-background-900/80 flex items-center justify-center">
                          <i className="ri-robot-2-line text-lg text-primary-400" />
                        </div>
                        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-10 h-10 rounded-lg border border-accent-500/20 bg-background-900/80 flex items-center justify-center">
                          <i className="ri-flight-takeoff-line text-lg text-accent-400" />
                        </div>
                        <div className="absolute top-1/2 left-0 -translate-y-1/2 w-10 h-10 rounded-lg border border-primary-500/20 bg-background-900/80 flex items-center justify-center">
                          <i className="ri-glasses-line text-lg text-primary-400" />
                        </div>
                        <div className="absolute top-1/2 right-0 -translate-y-1/2 w-10 h-10 rounded-lg border border-accent-500/20 bg-background-900/80 flex items-center justify-center">
                          <i className="ri-truck-line text-lg text-accent-400" />
                        </div>
                      </div>
                      <div className="relative z-10 flex flex-col items-center gap-3">
                        <div className="w-20 h-20 rounded-full border-2 border-primary-500/30 flex items-center justify-center animate-pulse bg-background-900/60 backdrop-blur-sm">
                          <i className="ri-radar-line text-3xl text-primary-400" />
                        </div>
                        <p className="text-xs text-primary-400 font-mono tracking-wider">Fleet Visualization</p>
                        <p className="text-[10px] text-foreground-600">异构终端阵列 · 实时渲染中</p>
                      </div>
                      <div className="absolute top-4 left-4 w-8 h-8 border-l-2 border-t-2 border-primary-500/30" />
                      <div className="absolute top-4 right-4 w-8 h-8 border-r-2 border-t-2 border-primary-500/30" />
                      <div className="absolute bottom-4 left-4 w-8 h-8 border-l-2 border-b-2 border-primary-500/30" />
                      <div className="absolute bottom-4 right-4 w-8 h-8 border-r-2 border-b-2 border-primary-500/30" />
                    </div>
                  ) : (
                    <>
                      <img
                        src={fleetHeroFrames[0]}
                        alt=""
                        className="absolute inset-0 w-full h-full object-cover flipbook-frame animate-flipbook-f1"
                        onLoad={() => setImageLoaded(true)}
                        onError={() => setImageError(true)}
                      />
                      <img
                        src={fleetHeroFrames[1]}
                        alt=""
                        className="absolute inset-0 w-full h-full object-cover flipbook-frame animate-flipbook-f2"
                      />
                      <img
                        src={fleetHeroFrames[2]}
                        alt=""
                        className="absolute inset-0 w-full h-full object-cover flipbook-frame animate-flipbook-f3"
                      />
                    </>
                  )}

                  {imageLoaded && !imageError && (
                    <div className="absolute inset-0 pointer-events-none overflow-hidden">
                      <div className="absolute left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-primary-400/50 to-transparent animate-scan-sweep z-20" />

                      <svg className="absolute inset-0 w-full h-full z-10" style={{ opacity: 0.45 }}>
                        <line x1="48%" y1="32%" x2="34%" y2="74%" stroke="rgba(217,162,78,0.45)" strokeWidth="1.5" strokeDasharray="4 5" className="animate-dash-flow" />
                        <line x1="52%" y1="32%" x2="66%" y2="74%" stroke="rgba(217,162,78,0.4)" strokeWidth="1.5" strokeDasharray="4 5" className="animate-dash-flow" style={{ animationDelay: '0.3s' }} />
                        <line x1="50%" y1="30%" x2="57%" y2="12%" stroke="rgba(217,162,78,0.35)" strokeWidth="1.5" strokeDasharray="4 5" className="animate-dash-flow" style={{ animationDelay: '0.6s' }} />
                        <line x1="48%" y1="34%" x2="82%" y2="64%" stroke="rgba(217,162,78,0.3)" strokeWidth="1" strokeDasharray="4 5" className="animate-dash-flow" style={{ animationDelay: '0.9s' }} />
                        <line x1="36%" y1="76%" x2="64%" y2="76%" stroke="rgba(217,162,78,0.25)" strokeWidth="1" strokeDasharray="3 6" className="animate-dash-flow" style={{ animationDelay: '1.2s' }} />
                        <line x1="58%" y1="14%" x2="84%" y2="66%" stroke="rgba(217,162,78,0.2)" strokeWidth="1" strokeDasharray="3 6" className="animate-dash-flow" style={{ animationDelay: '1.5s' }} />
                      </svg>

                      <svg className="absolute inset-0 w-full h-full pointer-events-none" style={{ zIndex: 11, opacity: 0.4 }}>
                        <line x1="46%" y1="32%" x2="34%" y2="74%" stroke="rgba(217,162,78,0.45)" strokeWidth="2" className="animate-mesh-beam" />
                        <line x1="52%" y1="32%" x2="66%" y2="74%" stroke="rgba(217,162,78,0.4)" strokeWidth="2" className="animate-mesh-beam" style={{ animationDelay: '0.3s' }} />
                        <line x1="50%" y1="30%" x2="57%" y2="12%" stroke="rgba(217,162,78,0.3)" strokeWidth="2" className="animate-mesh-beam" style={{ animationDelay: '0.6s' }} />
                        <line x1="48%" y1="34%" x2="82%" y2="64%" stroke="rgba(217,162,78,0.25)" strokeWidth="1.5" className="animate-mesh-beam" style={{ animationDelay: '0.9s' }} />
                        <line x1="36%" y1="76%" x2="64%" y2="76%" stroke="rgba(217,162,78,0.2)" strokeWidth="1.5" className="animate-mesh-beam" style={{ animationDelay: '1.2s' }} />
                      </svg>

                      {/* 这里原本还叠着六个终端名标签和三个状态芯片(LINK/SYNC/COORD)。
                          全部删掉:hero 素材**本身就烧进了 HUD**(LINK: OK / SYNC: 12ms /
                          NODES: 7,以及底部那条「异构终端舰队 实时协同中」),再叠一层
                          就是同一句话说两遍,而且两层位置互相打架 —— 截图里
                          「翼 无人机」正压在「LINK: 6 NODES OK」上。
                          留下扫描线与网格光束:纯装饰、没有文字,不会重复。 */}
                    </div>
                  )}
                </div>

                {!imageLoaded && !imageError && (
                  <div className="absolute inset-0 z-20 rounded-2xl bg-background-900 border border-background-800/60 flex items-center justify-center">
                    <div className="flex flex-col items-center gap-2">
                      <span className="w-8 h-8 flex items-center justify-center">
                        <i className="ri-loader-4-line text-xl text-primary-400 animate-spin" />
                      </span>
                      <span className="text-[10px] text-foreground-600 font-mono">Loading fleet...</span>
                    </div>
                  </div>
                )}

                {/* 底部那颗胶囊删掉:素材底部已经烧进了同一句文案 */}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 z-10 animate-scroll-bounce">
        <span className="text-[10px] font-mono text-foreground-600">SCROLL</span>
        <div className="w-px h-6 bg-gradient-to-b from-primary-500/60 to-transparent" />
      </div>
    </section>
  );
}

export { MouseGlow };